import React, { useState, useMemo } from 'react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell
} from 'recharts';
import {
    LineChart as LineChartIcon, MessageSquare, User, CalendarCheck,
    CalendarDays, DoorOpen, Stethoscope, Activity, Droplets,
    Users, UserPlus, Trash2, Info, Search, X, CalendarPlus, Menu, Moon, Sun, CircleSlash,
    ChevronLeft, ChevronRight, ChevronDown, Filter, RefreshCw, Plus, AlertTriangle,
    Clock, MapPin, Edit, Hospital, CheckCircle
} from 'lucide-react';
import Layout from '../../Layout'
import { useNavigate } from "react-router-dom";

const DAYS_OF_WEEK = [
    { day: 'THỨ 2', date: '10' },
    { day: 'THỨ 3', date: '11' },
    { day: 'THỨ 4', date: '12' },
    { day: 'THỨ 5', date: '13' },
    { day: 'THỨ 6', date: '14' },
    { day: 'THỨ 7', date: '15' },
    { day: 'CHỦ NHẬT', date: '16' },
];

const INITIAL_ROOMS = [
    { id: 'R1', name: 'Gold Meeting Room', status: 'Sẵn sàng', type: 'Phòng mổ vô trùng', capacity: 'Tiêu chuẩn', totalSurgeries: 14 },
    { id: 'R2', name: 'NTL Diamond', status: 'Đang phẫu thuật', type: 'Phòng đại phẫu', capacity: 'Cao cấp VIP', totalSurgeries: 22 },
    { id: 'R3', name: 'Phòng Advance', status: 'Khử trùng', type: 'Phòng tiểu phẫu', capacity: 'Tiêu chuẩn', totalSurgeries: 9 },
    { id: 'R4', name: 'Phòng họp Diamond', status: 'Sẵn sàng', type: 'Phòng mổ vô trùng', capacity: 'Tiêu chuẩn', totalSurgeries: 18 },
    { id: 'R5', name: 'Ruby Meeting Room', status: 'Bảo trì', type: 'Phòng đại phẫu', capacity: 'Chuyên sâu', totalSurgeries: 5 },
    { id: 'R6', name: 'Silver Meeting Room', status: 'Sẵn sàng', type: 'Phòng tiểu phẫu', capacity: 'Tiêu chuẩn', totalSurgeries: 11 },
];

const INITIAL_STAFF = [
    { id: 'S1', name: 'BS. Lê Minh Sơn', role: 'Bác sĩ phẫu thuật', status: 'Đang mổ', avatar: 'S' },
    { id: 'S2', name: 'BS. Trần Kim Yến', role: 'Bác sĩ gây mê', status: 'Sẵn sàng', avatar: 'Y' },
    { id: 'S3', name: 'YT. Nguyễn Thị An', role: 'Y tá', status: 'Sẵn sàng', avatar: 'A' },
    { id: 'S4', name: 'YT. Phạm Quỳnh', role: 'Y tá', status: 'Sẵn sàng', avatar: 'Q' },
    { id: 'S5', name: 'BS. Hoàng Tuấn', role: 'Bác sĩ phẫu thuật', status: 'Sẵn sàng', avatar: 'T' },
    { id: 'S6', name: 'BS. Vũ Đức Anh', role: 'Bác sĩ gây mê', status: 'Sẵn sàng', avatar: 'D' },
    { id: 'S7', name: 'YT. Lê Ngọc Hà', role: 'Y tá', status: 'Đang mổ', avatar: 'H' },
];

const INITIAL_SURGERIES = [
    { id: 'Surg1', roomId: 'R4', dayIndex: 0, title: 'Nâng mũi Sline bọc sụn', patient: 'Nguyễn Văn A', time: '08:00 - 10:00', status: 'Đã duyệt', team: ['S1', 'S2', 'S3'], warning: false, warningMsg: '' },
    { id: 'Surg2', roomId: 'R4', dayIndex: 0, title: 'Cắt mí Plasma Hàn Quốc', patient: 'Trần Thanh Tâm', time: '14:00 - 15:30', status: 'Chờ duyệt', team: ['S1', 'S4'], warning: true, warningMsg: 'Thiếu Bác sĩ gây mê' },
    { id: 'Surg3', roomId: 'R4', dayIndex: 1, title: 'Tạo hình cằm V-line', patient: 'Trần Thị B', time: '11:27 - 12:27', status: 'Đã duyệt', team: ['S5', 'S2', 'S4'], warning: false, warningMsg: '' },
    { id: 'Surg4', roomId: 'R2', dayIndex: 2, title: 'Nâng ngực K-Pop VIP', patient: 'Hồ Mỹ Nhàn', time: '09:00 - 12:00', status: 'Đã duyệt', team: ['S1', 'S3', 'S5', 'S2'], warning: false, warningMsg: '' },
    { id: 'Surg5', roomId: 'R2', dayIndex: 2, title: 'Hút mỡ bụng công nguyên thể', patient: 'Lê Ngọc Ánh', time: '13:30 - 15:30', status: 'Cảnh báo', team: ['S2'], warning: true, warningMsg: 'Thiếu BS Phẫu thuật & Y tá' },
    { id: 'Surg6', roomId: 'R1', dayIndex: 4, title: 'Cấy mỡ mặt Baby Face', patient: 'Phạm Thu Trang', time: '10:00 - 11:30', status: 'Đã duyệt', team: ['S5', 'S6', 'S7'], warning: false, warningMsg: '' }
];

const PIE_COLORS = ['#2563eb', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];
const READINESS_COLORS = ['#10b981', '#ef4444', '#f59e0b'];

export default function App() {
    const navigate = useNavigate();

    // Theme & Layout State
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('schedule');

    // Data State
    const [rooms, setRooms] = useState(INITIAL_ROOMS);
    const [surgeries, setSurgeries] = useState(INITIAL_SURGERIES);
    const [staffList] = useState(INITIAL_STAFF); // Keeping static for this demo

    // Filters & Controls State
    const [currentMonth, setCurrentMonth] = useState(8);
    const [currentYear, setCurrentYear] = useState(2026);
    const [isMonthPickerOpen, setIsMonthPickerOpen] = useState(false);
    const [roomStatusFilter, setRoomStatusFilter] = useState('All');

    const [staffSearchQuery, setStaffSearchQuery] = useState('');
    const [staffRoleFilter, setStaffRoleFilter] = useState('All');

    // Modals State
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [isAddRoomModalOpen, setIsAddRoomModalOpen] = useState(false);

    const [selectedRoomForCreate, setSelectedRoomForCreate] = useState('');
    const [selectedDateForCreate, setSelectedDateForCreate] = useState('');
    const [activeSurgeryId, setActiveSurgeryId] = useState(null);

    // Form State (Create Surgery)
    const [newSurgeryTitle, setNewSurgeryTitle] = useState('');
    const [newSurgeryPatient, setNewSurgeryPatient] = useState('');
    const [newSurgeryTime, setNewSurgeryTime] = useState('08:00 - 10:00');
    const [newSurgeryStaff, setNewSurgeryStaff] = useState([]);
    const [createStaffSearch, setCreateStaffSearch] = useState('');
    const [createStaffRoleFilter, setCreateStaffRoleFilter] = useState('all');

    // Form State (Add Room)
    const [newRoomName, setNewRoomName] = useState('');
    const [newRoomType, setNewRoomType] = useState('Phòng mổ vô trùng');
    const [newRoomCapacity, setNewRoomCapacity] = useState('Tiêu chuẩn');

    // Toasts
    const [toasts, setToasts] = useState([]);

    const showToast = (message, type = 'info') => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => {
            setToasts(prev => prev.filter(t => t.id !== id));
        }, 3000);
    };

    const getStatusConfig = (status) => {
        switch (status) {
            case 'Sẵn sàng': return { color: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:border-emerald-800 dark:text-emerald-400', dot: 'bg-emerald-500' };
            case 'Đang phẫu thuật': return { color: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:border-rose-800 dark:text-rose-400', dot: 'bg-rose-500 animate-pulse' };
            case 'Khử trùng': return { color: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:border-amber-800 dark:text-amber-400', dot: 'bg-amber-500' };
            case 'Bảo trì': default: return { color: 'bg-slate-100 text-slate-600 border-slate-300 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-400', dot: 'bg-slate-400' };
        }
    };

    // derived data
    const filteredRooms = useMemo(() => {
        return roomStatusFilter === 'All' ? rooms : rooms.filter(r => r.status === roomStatusFilter);
    }, [rooms, roomStatusFilter]);

    const warningSurgeries = useMemo(() => surgeries.filter(s => s.warning), [surgeries]);

    const filteredStaffList = useMemo(() => {
        return staffList.filter(s => {
            const matchName = s.name.toLowerCase().includes(staffSearchQuery.toLowerCase());
            const matchRole = staffRoleFilter === 'All' || s.role === staffRoleFilter;
            return matchName && matchRole;
        });
    }, [staffList, staffSearchQuery, staffRoleFilter]);

    const modalStaffList = useMemo(() => {
        return staffList.filter(s => {
            const matchName = s.name.toLowerCase().includes(createStaffSearch.toLowerCase());
            const matchRole = createStaffRoleFilter === 'all' || s.role === createStaffRoleFilter;
            return matchName && matchRole;
        });
    }, [staffList, createStaffSearch, createStaffRoleFilter]);

    const handleOpenCreateSurgery = (roomId = 'R1', dateStr = '10/08/2026') => {
        setSelectedRoomForCreate(roomId);
        setSelectedDateForCreate(dateStr);
        setNewSurgeryTitle('');
        setNewSurgeryPatient('');
        setNewSurgeryStaff([]);
        setIsCreateModalOpen(true);
    };

    const updateRoomStatusDirect = (roomId, newStatus) => {
        setRooms(prev => prev.map(rm => rm.id === roomId ? { ...rm, status: newStatus } : rm));
        showToast(`Đã cập nhật trạng thái phòng thành: ${newStatus}`);
    };

    const activeSurgery = surgeries.find(s => s.id === activeSurgeryId);

    const roomStats = [
        { icon: Hospital, color: 'blue', label: 'Tổng phòng', val: rooms.length },
        { icon: CheckCircle, color: 'emerald', label: 'Sẵn sàng', val: rooms.filter(r => r.status === 'Sẵn sàng').length },
        { icon: Activity, color: 'rose', label: 'Đang mổ', val: rooms.filter(r => r.status === 'Đang phẫu thuật').length },
        { icon: Droplets, color: 'amber', label: 'Vệ sinh/Bảo trì', val: rooms.filter(r => ['Khử trùng', 'Bảo trì'].includes(r.status)).length }
    ];

    const handleSaveRoom = () => {
        if (!newRoomName.trim()) {
            showToast("Vui lòng nhập tên phòng mổ!", "error");
            return;
        }
        const newRoom = {
            id: 'R' + (rooms.length + 1),
            name: newRoomName,
            status: 'Sẵn sàng',
            type: newRoomType,
            capacity: newRoomCapacity,
            totalSurgeries: 0
        };
        setRooms([...rooms, newRoom]);
        setIsAddRoomModalOpen(false);
        setNewRoomName('');
        showToast(`Đã thêm phòng mổ ${newRoomName} mới!`);
    };

    const handleSaveSurgery = () => {
        if (!newSurgeryTitle.trim() || !newSurgeryPatient.trim()) {
            showToast("Vui lòng nhập đầy đủ tên bệnh nhân và dịch vụ phẫu thuật!", "error");
            return;
        }

        const assignedRoles = newSurgeryStaff.map(id => staffList.find(s => s.id === id)?.role);
        const hasSurgeon = assignedRoles.includes('Bác sĩ phẫu thuật');
        const hasAnesthetist = assignedRoles.includes('Bác sĩ gây mê');
        const hasNurse = assignedRoles.includes('Y tá');

        let warning = false;
        let warningMsg = '';
        if (!hasSurgeon || !hasAnesthetist || !hasNurse) {
            warning = true;
            const missing = [];
            if (!hasSurgeon) missing.push('BS Phẫu thuật');
            if (!hasAnesthetist) missing.push('BS Gây mê');
            if (!hasNurse) missing.push('Y tá');
            warningMsg = 'Thiếu ' + missing.join(', ');
        }

        const newSurg = {
            id: 'Surg' + (surgeries.length + 1),
            roomId: selectedRoomForCreate,
            dayIndex: 0, // Simplified to first day for demo adding
            title: newSurgeryTitle,
            patient: newSurgeryPatient,
            time: newSurgeryTime,
            status: warning ? 'Chờ duyệt' : 'Đã duyệt',
            team: newSurgeryStaff,
            warning,
            warningMsg
        };

        setSurgeries([...surgeries, newSurg]);
        setIsCreateModalOpen(false);
        showToast(warning ? "Đã tạo ca mổ (Có cảnh báo thiếu nhân sự)!" : "Tạo lịch mổ mới thành công!");
    };

    return (
        <Layout>
            <div className="flex h-full w-full overflow-hidden relative">

                {/* MAIN CONTENT */}
                <main className="flex-1 flex flex-col h-full overflow-hidden relative">
                    {/* VIEWS */}
                    <div className="h-full pb-4">
                        <div className="mx-auto space-y-6">
                            <div className="bg-white dark:bg-slate-900 p-6 rounded-xl shadow-xs border border-slate-200 dark:border-slate-800 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                                <div>
                                    <h2 className="text-xl font-extrabold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                                        <DoorOpen className="text-emerald-600 dark:text-emerald-500" /> Quản Lý Danh Sách & Trạng Thái
                                    </h2>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                        Theo dõi chi tiết năng suất và cập nhật trạng thái hoạt động của các phòng mổ.
                                    </p>
                                </div>
                                <button onClick={() => setIsAddRoomModalOpen(true)} className="px-4 py-2 bg-[#3e91ab] text-white font-bold text-xs rounded-lg shadow-sm flex items-center gap-2 transition-all">
                                    <Plus size={16} /> Thêm phòng mổ mới
                                </button>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                {roomStats.map((stat, i) => {
                                    const Icon = stat.icon;
                                    return (
                                        <div key={i} className="bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800 shadow-xs flex flex-col sm:flex-row items-center sm:items-start gap-4 text-center sm:text-left">
                                            <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-${stat.color}-50 dark:bg-${stat.color}-900/20 text-${stat.color}-600 dark:text-${stat.color}-400 flex items-center justify-center font-bold text-xl shrink-0`}>
                                                <Icon size={24} />
                                            </div>
                                            <div>
                                                <div className="text-[10px] sm:text-xs font-bold text-slate-400 uppercase mt-1 sm:mt-0">{stat.label}</div>
                                                <div className={`text-xl sm:text-2xl font-black text-${stat.color}-600 dark:text-${stat.color}-400`}>{stat.val}</div>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {rooms.map(room => (
                                    <div key={room.id} className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-xs hover:shadow-md transition-all p-5 flex flex-col justify-between">
                                        <div>
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">{room.id} • {room.capacity}</span>
                                                <span className="px-2 py-0.5 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-[10px] font-bold rounded">{room.type}</span>
                                            </div>
                                            <h3 className="text-base font-extrabold text-slate-900 dark:text-slate-100 mb-1">{room.name}</h3>
                                            <p className="text-xs text-slate-500 dark:text-slate-400">Đã thực hiện <strong className="text-slate-700 dark:text-slate-300">{room.totalSurgeries} ca</strong> trong tháng.</p>
                                        </div>
                                        <div className="mt-5 pt-4 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
                                            <div>
                                                <select value={room.status} onChange={(e) => updateRoomStatusDirect(room.id, e.target.value)}
                                                    className="border border-slate-300 dark:border-slate-700 rounded-lg px-2.5 py-1 text-xs font-bold bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-200 focus:border-emerald-500 outline-none cursor-pointer">
                                                    <option value="Sẵn sàng">🟢 Sẵn sàng</option>
                                                    <option value="Đang phẫu thuật">🔴 Đang phẫu thuật</option>
                                                    <option value="Khử trùng">🟡 Khử trùng</option>
                                                    <option value="Bảo trì">⚪ Bảo trì</option>
                                                </select>
                                            </div>
                                            <button onClick={() => navigate(`/implementation-schedule`)} className="px-3 py-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 font-bold rounded-lg text-xs hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors">
                                                + Lên lịch
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </main>
            </div>

            {/* MODAL: Create Surgery */}
            {isCreateModalOpen && (
                <div className="fixed inset-0 bg-slate-900/60 z-[100] flex items-center justify-center backdrop-blur-sm p-4 overflow-y-auto">
                    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-[620px] overflow-hidden flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-150 border border-slate-200 dark:border-slate-700">
                        <div className="px-6 py-4 bg-blue-600 text-white flex justify-between items-center">
                            <h3 className="text-base font-bold flex items-center gap-2">
                                <CalendarPlus size={18} /> Tạo Lịch Phẫu Thuật Mới
                            </h3>
                            <button onClick={() => setIsCreateModalOpen(false)} className="w-8 h-8 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors">
                                <X size={16} />
                            </button>
                        </div>

                        <div className="p-6 overflow-y-auto space-y-4 text-xs custom-scrollbar">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block font-bold text-slate-700 dark:text-slate-300 mb-1">Ngày thực hiện</label>
                                    <input type="text" value={selectedDateForCreate} readOnly className="w-full border border-slate-300 dark:border-slate-700 rounded-lg p-2.5 font-semibold bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400 outline-none" />
                                </div>
                                <div>
                                    <label className="block font-bold text-slate-700 dark:text-slate-300 mb-1">Phòng mổ chỉ định</label>
                                    <select value={selectedRoomForCreate} onChange={(e) => setSelectedRoomForCreate(e.target.value)} className="w-full border border-slate-300 dark:border-slate-700 rounded-lg p-2.5 font-semibold bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 outline-none focus:border-blue-500">
                                        {rooms.map(r => <option key={r.id} value={r.id}>{r.name} ({r.status})</option>)}
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block font-bold text-slate-700 dark:text-slate-300 mb-1">Khung giờ</label>
                                    <select value={newSurgeryTime} onChange={(e) => setNewSurgeryTime(e.target.value)} className="w-full border border-slate-300 dark:border-slate-700 rounded-lg p-2.5 font-semibold bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 outline-none focus:border-blue-500">
                                        <option value="08:00 - 10:00">08:00 - 10:00</option>
                                        <option value="10:30 - 12:30">10:30 - 12:30</option>
                                        <option value="13:30 - 15:30">13:30 - 15:30</option>
                                        <option value="15:30 - 17:30">15:30 - 17:30</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block font-bold text-slate-700 dark:text-slate-300 mb-1">Bệnh nhân</label>
                                    <input type="text" value={newSurgeryPatient} onChange={(e) => setNewSurgeryPatient(e.target.value)} placeholder="Nhập tên bệnh nhân..." className="w-full border border-slate-300 dark:border-slate-700 rounded-lg p-2.5 font-semibold bg-transparent text-slate-800 dark:text-slate-200 outline-none focus:border-blue-500" />
                                </div>
                            </div>

                            <div>
                                <label className="block font-bold text-slate-700 dark:text-slate-300 mb-1">Dịch vụ phẫu thuật</label>
                                <input type="text" value={newSurgeryTitle} onChange={(e) => setNewSurgeryTitle(e.target.value)} placeholder="VD: Nâng mũi Sline..." className="w-full border border-slate-300 dark:border-slate-700 rounded-lg p-2.5 font-semibold bg-transparent text-slate-800 dark:text-slate-200 outline-none focus:border-blue-500" />
                            </div>

                            <div className="border-t border-slate-200 dark:border-slate-700 pt-4 mt-2">
                                <div className="flex items-center justify-between mb-2">
                                    <label className="block text-sm font-extrabold text-slate-800 dark:text-slate-200">Đề xuất Ê-kíp</label>
                                    <span className="text-[11px] font-semibold text-rose-500 flex items-center gap-1">
                                        <Info size={12} /> Tối thiểu: 1 BS PT, 1 BS GM, 1 Y tá
                                    </span>
                                </div>

                                <div className="flex gap-2 mb-3">
                                    <div className="relative flex-1">
                                        <Search size={14} className="absolute left-3 top-2.5 text-slate-400" />
                                        <input type="text" value={createStaffSearch} onChange={(e) => setCreateStaffSearch(e.target.value)} placeholder="Tìm tên bác sĩ, y tá..." className="w-full border border-slate-300 dark:border-slate-700 rounded-lg pl-8 p-2 !h-[38px] text-xs bg-transparent text-slate-800 dark:text-slate-200 outline-none focus:border-blue-500" />
                                    </div>
                                    <select value={createStaffRoleFilter} onChange={(e) => setCreateStaffRoleFilter(e.target.value)} className="border border-slate-300 dark:border-slate-700 rounded-lg p-2 text-xs font-semibold bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 outline-none w-[140px] sm:w-[170px]">
                                        <option value="all">Tất cả vị trí</option>
                                        <option value="Bác sĩ phẫu thuật">BS Phẫu thuật</option>
                                        <option value="Bác sĩ gây mê">BS Gây mê</option>
                                        <option value="Y tá">Y tá</option>
                                    </select>
                                </div>

                                <div className="border border-slate-200 dark:border-slate-700 rounded-lg max-h-[170px] overflow-y-auto p-2 bg-slate-50 dark:bg-slate-800/50 space-y-1 custom-scrollbar">
                                    {modalStaffList.map(staff => (
                                        <label key={staff.id} className="flex items-center justify-between p-2 hover:bg-white dark:hover:bg-slate-700 rounded cursor-pointer border border-transparent hover:border-slate-200 dark:hover:border-slate-600 transition-colors">
                                            <div className="flex items-center gap-2">
                                                <input type="checkbox" checked={newSurgeryStaff.includes(staff.id)} onChange={(e) => {
                                                    if (e.target.checked) setNewSurgeryStaff([...newSurgeryStaff, staff.id]);
                                                    else setNewSurgeryStaff(newSurgeryStaff.filter(id => id !== staff.id));
                                                }} className="w-4 h-4 text-blue-600 rounded border-slate-300" />
                                                <div>
                                                    <div className="text-xs font-bold text-slate-700 dark:text-slate-200">{staff.name}</div>
                                                    <div className="text-[10px] text-slate-500 dark:text-slate-400">{staff.role}</div>
                                                </div>
                                            </div>
                                            <span className="text-[10px] font-bold text-slate-400">{staff.status}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="p-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 flex justify-end gap-3">
                            <button onClick={() => setIsCreateModalOpen(false)} className="px-5 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-lg font-bold hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                                Hủy
                            </button>
                            <button onClick={handleSaveSurgery} className="px-6 py-2 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition-colors shadow-sm">
                                Tạo Lịch
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL: Add Room */}
            {isAddRoomModalOpen && (
                <div className="fixed inset-0 bg-slate-900/60 z-[100] flex items-center justify-center backdrop-blur-sm p-4">
                    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-[480px] overflow-hidden flex flex-col animate-in zoom-in-95 duration-150 border border-slate-200 dark:border-slate-700">
                        <div className="px-6 py-4 bg-[#3e91ab] text-white flex justify-between items-center">
                            <h3 className="text-base font-bold flex items-center gap-2">
                                <DoorOpen size={18} /> Thêm Phòng Mổ Mới
                            </h3>
                            <button onClick={() => setIsAddRoomModalOpen(false)} className="w-8 h-8 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors">
                                <X size={16} />
                            </button>
                        </div>

                        <div className="p-6 space-y-4 text-xs">
                            <div>
                                <label className="block font-bold text-slate-700 dark:text-slate-300 mb-1">Tên phòng mổ</label>
                                <input type="text" value={newRoomName} onChange={e => setNewRoomName(e.target.value)} placeholder="VD: Phòng mổ VIP 03..." className="w-full border border-slate-300 dark:border-slate-700 rounded-lg p-2.5 font-semibold bg-transparent text-slate-800 dark:text-slate-200 outline-none focus:border-emerald-500" />
                            </div>

                            <div>
                                <label className="block font-bold text-slate-700 dark:text-slate-300 mb-1">Loại phòng</label>
                                <select value={newRoomType} onChange={e => setNewRoomType(e.target.value)} className="w-full border border-slate-300 dark:border-slate-700 rounded-lg p-2.5 font-semibold bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 outline-none focus:border-emerald-500">
                                    <option value="Phòng mổ vô trùng">Phòng mổ vô trùng</option>
                                    <option value="Phòng đại phẫu">Phòng đại phẫu</option>
                                    <option value="Phòng tiểu phẫu">Phòng tiểu phẫu</option>
                                </select>
                            </div>

                            <div>
                                <label className="block font-bold text-slate-700 dark:text-slate-300 mb-1">Sức chứa & Tiêu chuẩn</label>
                                <select value={newRoomCapacity} onChange={e => setNewRoomCapacity(e.target.value)} className="w-full border border-slate-300 dark:border-slate-700 rounded-lg p-2.5 font-semibold bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 outline-none focus:border-emerald-500">
                                    <option value="Tiêu chuẩn">Tiêu chuẩn</option>
                                    <option value="Cao cấp VIP">Cao cấp VIP</option>
                                    <option value="Chuyên sâu">Chuyên sâu</option>
                                </select>
                            </div>
                        </div>

                        <div className="p-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 flex justify-end gap-3">
                            <button onClick={() => setIsAddRoomModalOpen(false)} className="px-5 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-lg font-bold hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                                Hủy
                            </button>
                            <button onClick={handleSaveRoom} className="px-6 py-2 bg-[#3e91ab] text-white rounded-lg font-bold hover:bg-emerald-700 transition-colors shadow-sm">
                                Lưu Phòng
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Global Toast Container */}
            <div className="fixed bottom-5 right-5 z-[200] flex flex-col gap-2 pointer-events-none">
                {toasts.map(toast => (
                    <div key={toast.id} className={`p-3 rounded-xl shadow-xl text-xs font-bold text-white flex items-center gap-2 pointer-events-auto transform transition-all animate-in slide-in-from-bottom-5 ${toast.type === 'error' ? 'bg-rose-600' : 'bg-slate-800 dark:bg-slate-200 dark:text-slate-900'}`}>
                        {toast.type === 'error' ? <AlertTriangle size={16} /> : <CheckCircle size={16} />} {toast.message}
                    </div>
                ))}
            </div>

            {/* Global Styles specific to this app component */}
            <style dangerouslySetInnerHTML={{
                __html: `
                        .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
                        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                        .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
                        .dark .custom-scrollbar::-webkit-scrollbar-thumb { background: #475569; }
                        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
                    `}}
            />
        </Layout>
    );
}